/**
 * registerServiceSections — registers all section variants for the service vertical.
 *
 * Call once at app boot (or top of the generated Home.tsx) before any SectionRenderer mounts.
 * Cross-vertical sections (hero, story, gallery, cta, map, social, hours, testimonials,
 * parallax) are also registered here so a service app doesn't need to call both functions.
 * The registry is a global map — duplicate calls are idempotent.
 */
import { registerSection } from "./registry.js";

// Cross-vertical (shared with food-bev)
import { HeroSplit } from "./sections/hero/HeroSplit.js";
import { HeroVideo } from "./sections/hero/HeroVideo.js";
import { HeroMinimal } from "./sections/hero/HeroMinimal.js";
import { HeroFullBleed } from "./sections/hero/HeroFullBleed.js";
import { StorySplit } from "./sections/story/StorySplit.js";
import { StoryTestimonial } from "./sections/story/StoryTestimonial.js";
import { StoryStat } from "./sections/story/StoryStat.js";
import { GalleryGrid } from "./sections/gallery/GalleryGrid.js";
import { GalleryMasonry } from "./sections/gallery/GalleryMasonry.js";
import { HoursSimple } from "./sections/hours/HoursSimple.js";
import { HoursColumns } from "./sections/hours/HoursColumns.js";
import { HoursCompact } from "./sections/hours/HoursCompact.js";
import { MapEmbed } from "./sections/map/MapEmbed.js";
import { MapSplit } from "./sections/map/MapSplit.js";
import { CtaBanner } from "./sections/cta/CtaBanner.js";
import { CtaSplit } from "./sections/cta/CtaSplit.js";
import { CtaStacked } from "./sections/cta/CtaStacked.js";
import { SocialLinks } from "./sections/social/SocialLinks.js";
import { SocialGrid } from "./sections/social/SocialGrid.js";
import { ParallaxBreak } from "./sections/parallax/ParallaxBreak.js";
import { PullQuote } from "./sections/custom/PullQuote.js";
import { MarqueeStrip } from "./sections/custom/MarqueeStrip.js";
import { PricingTiers } from "./sections/pricing/PricingTiers.js";

// Service-vertical exclusive
import { ServicesTiers } from "./sections/services/ServicesTiers.js";
import { ServicesTiersDeduped } from "./sections/services/ServicesTiersDeduped.js";
import { ServicesList } from "./sections/services/ServicesList.js";
import { ServicesNumbered } from "./sections/services/ServicesNumbered.js";
import { QuoteForm } from "./sections/quote/QuoteForm.js";
import { QuoteCallback } from "./sections/quote/QuoteCallback.js";
import { ServiceAreaRadius } from "./sections/serviceArea/ServiceAreaRadius.js";
import { ServiceAreaList } from "./sections/serviceArea/ServiceAreaList.js";
import { BeforeAfter } from "./sections/gallery/BeforeAfter.js";

// Booking sections
import { ReservationsInline } from "./sections/reservations/ReservationsInline.js";
import { ReservationsModal } from "./sections/reservations/ReservationsModal.js";

let registered = false;

export function registerServiceSections() {
  if (registered) return;
  registered = true;

  // Hero variants (shared)
  registerSection("hero", "hero-split", HeroSplit);
  registerSection("hero", "hero-video", HeroVideo);
  registerSection("hero", "hero-minimal", HeroMinimal);
  registerSection("hero", "hero-full-bleed", HeroFullBleed);

  // Service catalog
  registerSection("services", "services-tiers", ServicesTiers);
  registerSection("services", "services-tiers-deduped", ServicesTiersDeduped);
  registerSection("services", "services-list", ServicesList);
  registerSection("services", "services-numbered", ServicesNumbered);

  // Quote / contact form
  registerSection("quote", "quote-form", QuoteForm);
  registerSection("quote", "quote-callback", QuoteCallback);

  // Booking
  registerSection("reservations", "reservations-inline", ReservationsInline);
  registerSection("reservations", "reservations-modal", ReservationsModal);

  // Service area
  registerSection("service-area", "service-area-radius", ServiceAreaRadius);
  registerSection("service-area", "service-area-list", ServiceAreaList);

  // Gallery variants
  registerSection("gallery", "gallery-grid", GalleryGrid);
  registerSection("gallery", "gallery-masonry", GalleryMasonry);
  registerSection("gallery", "before-after", BeforeAfter);

  // Story variants
  registerSection("story", "story-split", StorySplit);
  registerSection("story", "story-testimonial", StoryTestimonial);
  registerSection("story", "story-stat", StoryStat);

  // Hours variants
  registerSection("hours", "hours-simple", HoursSimple);
  registerSection("hours", "hours-columns", HoursColumns);
  registerSection("hours", "hours-compact", HoursCompact);

  // Map variants
  registerSection("map", "map-embed", MapEmbed);
  registerSection("map", "map-split", MapSplit);

  // CTA variants
  registerSection("cta", "cta-banner", CtaBanner);
  registerSection("cta", "cta-split", CtaSplit);
  registerSection("cta", "cta-stacked", CtaStacked);

  // Social variants
  registerSection("social", "social-links", SocialLinks);
  registerSection("social", "social-grid", SocialGrid);

  // Custom variants
  registerSection("custom", "parallax-break", ParallaxBreak);
  registerSection("custom", "pull-quote", PullQuote);
  registerSection("custom", "marquee-strip", MarqueeStrip);

  // SaaS pricing (platform upsell shown on trial sites)
  registerSection("pricing", "pricing-tiers", PricingTiers);
}
